import VideoWrapper from "@Components/activity/VideoWrapper";
import Feedback from "@Components/shared/Feedback";
import FeedbackForm from "@Components/shared/FeedbackForm";
import { DateTime, prettifyName } from "@Utils";
import { Card, Col, Divider, Empty, Row, Select } from "antd";
import moment from "moment";
import React from "react";
import { IAssessmentView } from "./types";

import PageHelmet from "@Components/shared/PageHelmet";
import useAdminSubmissions from "./hooks/useAdminSubmissions";
import DataItems from "@Components/shared/DataItems";
import LargeSpinner from "@Components/shared/LargeSpinner";

const AdminSubmissionsView: React.FC<IAssessmentView> = ({ tab }) => {
    const { data, isLoading, isDisabled, isSubmitting, template, submission, onSubmit } = useAdminSubmissions();

    const instances = data?.assessmentInstanceId?.list || [];

    return (
        <article className={`admin-submissions-view ${tab}-tab`}>
            <PageHelmet name={"Assessments Admin Submissions View"} />
            <section>
                {isLoading && <LargeSpinner />}
                {!isLoading && !!submission && (
                    <Row gutter={[16, 16]} justify="center">
                        <Col xs={24} sm={24} md={24} lg={10} xl={12} xxl={12}>
                            <Card className="newUI-subtle">
                                <Card.Grid className="full-width" hoverable={false}>
                                    <DataItems
                                        data={[
                                            {
                                                heading: "Assessment",
                                                content: submission.name,
                                            },
                                            {
                                                heading: "Description",
                                                content: submission.description,
                                            },
                                            {
                                                heading: "End Date",
                                                content: DateTime(submission.endDate).format("MMM Do YYYY"),
                                            },
                                        ]}
                                    />
                                    <Divider />
                                    <DataItems
                                        data={[
                                            {
                                                heading: "User",
                                                content:
                                                    instances.length < 2 ? (
                                                        submission.recipientName
                                                    ) : (
                                                        <Select
                                                            defaultValue={submission.assessmentInstanceId}
                                                            onChange={data?.assessmentInstanceId?.onSelect}
                                                            loading={data.assessmentInstanceId.isLoading}
                                                            showSearch
                                                        >
                                                            {instances.map((instance) => (
                                                                <Select.Option
                                                                    disabled={instance.status === "assigned"}
                                                                    key={instance.assessmentInstanceId}
                                                                    value={instance.assessmentInstanceId}
                                                                >
                                                                    {instance.recipientName}{" "}
                                                                    {instance.status === "assigned" &&
                                                                        "(Not submitted)"}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    ),
                                            },
                                            {
                                                heading: "Submitted",
                                                content: moment(submission.submissionDate).format("MMM Do YYYY"),
                                            },
                                            {
                                                heading: "Status",
                                                content: `${prettifyName(submission.status)} ${
                                                    submission.onTime ? "(on time)" : ""
                                                }`,
                                            },
                                        ]}
                                    />
                                    <Divider />
                                    <Row justify="center">
                                        <Select
                                            placeholder="Select activity..."
                                            onChange={data?.activityInstance?.onSelect}
                                        >
                                            {submission?.tasks?.map((task) => (
                                                <Select.Option
                                                    key={task.activityInstanceId}
                                                    value={task.activityInstanceId}
                                                    loading={data.activityInstance.isLoading}
                                                >
                                                    {`${task.activityLabel} (${task.count}/${task.quantity}) - ${moment(
                                                        task.creationDate
                                                    ).format("MMM Do YYYY, h:mm A")}`}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Row>
                                    <Row justify={"center"}>
                                        {!!data?.activityInstance?.videoActivity?.mediaUrl ? (
                                            <VideoWrapper
                                                activity={data?.activityInstance?.videoActivity}
                                                withoutComments
                                            />
                                        ) : (
                                            <Empty
                                                style={{ paddingTop: "1rem" }}
                                                description={
                                                    <span>
                                                        {!data?.activityInstance?.videoActivity
                                                            ? "Select an activity to review the video"
                                                            : "No video available"}
                                                    </span>
                                                }
                                            />
                                        )}
                                    </Row>
                                </Card.Grid>
                            </Card>
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={14} xl={12} xxl={12}>
                            {!!template && submission.status !== "assigned" && (
                                <Card className="newUI-subtle">
                                    <Card.Grid className="card" hoverable={false}>
                                        {(submission.status === "submitted" && (
                                            <FeedbackForm
                                                {...template}
                                                isDisabled={isDisabled}
                                                isLoading={isSubmitting}
                                                onSubmit={onSubmit}
                                            />
                                        )) || (
                                            <Feedback
                                                description={template.description}
                                                feedback={submission?.feedback}
                                                fields={template.fields}
                                                name={template.name}
                                            />
                                        )}
                                    </Card.Grid>
                                </Card>
                            )}
                        </Col>
                    </Row>
                )}
            </section>
        </article>
    );
};

export default AdminSubmissionsView;
